import React, { FunctionComponent, useState, useEffect } from 'react';

import { forEach, has, isFunction, map, sortBy } from 'lodash';
import { Button, FormControl } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import {
    DropdownPicker,
    Props as DropdownPickerProps,
    DropDownPickerOverlayArea,
    DropdownDataWithHeader,
    DropdownData,
} from 'common/dropdown-picker/DropdownPicker';
import { OutsideClick } from 'common/outside-click/OutsideClick';
import { expandTemplates, cancelExpandTemplates, Template, TemplateLevel } from 'common/templates/Templates';
import { extractVariables, cancelExtractVariables } from 'common/variables/Variables';
import { Overlay } from 'core/overlay/Overlay';

import 'common/templates/template-picker/TemplatePicker.scss';

const TemplateLevels: any = {
    1: 'SYSTEM',
    2: 'TENANT',
    3: 'LOCATION',
    4: 'USER',
};

export enum Sentiment {
    Positive = 1,
    Negative = 2,
    Neutral = 3,
    None = 4,
}

interface ExpandTemplateRequest {
    sourceID?: string;
    sentiment?: Sentiment;
    categoryIDs?: object;
    locationAttributes?: object;
    industryID?: object;
    roleIDs?: object;
    moduleGroup?: object;
    autoRespond?: boolean;
    url?: string;
    variableTypeAndObjectIDs?: object[];
    requiredLevels?: number[];
}

interface Handlers {
    onSelect: any;
    onClose?: any;
    onDataLoad?: any;
}

export interface Props extends Omit<DropdownPickerProps, 'dropdownData' | 'handlers'> {
    agencyId: number;
    tenantId: number;
    dropdownRef?: any;
    expandTemplateRequest?: ExpandTemplateRequest;
    extractVariablesTypes?: any;
    variableEditForm?: any;
    handlers?: Handlers;
    customLabelElement?: FunctionComponent<any>;
}

const ExtractVariableOverlayArea = 'template-picker-extract-variable-overlay-area';
const VariableRegex = /{([^}]*)}/;

export const TemplatePicker: FunctionComponent<Props> = ({
    agencyId,
    tenantId,
    dropdownRef,
    expandTemplateRequest = {},
    variableEditForm,
    extractVariablesTypes,
    handlers,
    searchOptions,
    displayOptions,
    enableAutoSelect,
    autoFocus,
    customLabelElement,
}): JSX.Element => {
    const { t } = useTranslation(['common/templates']);
    const [hasDataLoaded, setHasDataLoaded] = useState(false);
    const [templatesData, setTemplatesData] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
    const [displayVariableForm, setDisplayVariableForm] = useState(false);
    const [userDefinedVariables, setUserDefinedVariables] = useState<any>([]);

    useEffect(() => {
        if (!variableEditForm || !variableEditForm.userDefinedVariables) {
            const parseData = (inputData: Template[]): DropdownDataWithHeader[] => {
                const dataByLevels: any = {};

                const sortedInputData = sortBy(inputData, (inputItem) => {
                    if (inputItem.name) {
                        return inputItem.name.toLowerCase();
                    }
                    if (inputItem.text) {
                        return inputItem.text.toLowerCase();
                    }
                    return '';
                });

                forEach(sortedInputData, (inputItem): any => {
                    forEach(inputItem.levels, (levelItem): any => {
                        if (!has(dataByLevels, `[${levelItem}]`)) {
                            dataByLevels[levelItem] = {
                                header: t(TemplateLevels[levelItem]),
                                content: [],
                            };
                        }
                        dataByLevels[levelItem].content.push({
                            label: inputItem.name,
                            description: inputItem.text,
                            customLabelElement: customLabelElement?.(inputItem) ?? undefined,
                        });
                    });
                });

                const parsedData: DropdownDataWithHeader[] = [];

                const templateSortOrder: TemplateLevel[] = [
                    TemplateLevel.User,
                    TemplateLevel.Location,
                    TemplateLevel.Tenant,
                    TemplateLevel.System,
                ]; // USER, LOCATION, TENANT, SYSTEM

                forEach(templateSortOrder, (key: any) => {
                    if (dataByLevels[key]) {
                        parsedData.push(dataByLevels[key]);
                    }
                });
                return parsedData;
            };

            const getTemplates = async (): Promise<void> => {
                try {
                    const data: Template[] = await trackPromise(
                        expandTemplates(tenantId, {}, { expandTemplateRequest }),
                        DropDownPickerOverlayArea,
                    );
                    const parsedData: DropdownDataWithHeader[] = parseData(data);

                    setTemplatesData(parsedData as any);
                    setHasDataLoaded(true);
                    if (!autoFocus && handlers && handlers.onDataLoad) {
                        handlers.onDataLoad();
                    }
                } catch (error) {
                    handleClose();
                }
            };

            getTemplates();
        }

        return (): void => {
            if (isFunction(cancelExpandTemplates)) {
                cancelExpandTemplates();
            }
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (variableEditForm && variableEditForm.userDefinedVariables) {
            setSelectedTemplate({
                label: variableEditForm.label,
            });
            setDisplayVariableForm(true);
            setUserDefinedVariables(variableEditForm.userDefinedVariables);
        }
    }, [variableEditForm]);

    const handleSelection = async (value: DropdownData): Promise<void> => {
        if (value && VariableRegex.test(value.description!)) {
            setDisplayVariableForm(true);
            const variables = await trackPromise(
                extractVariables(tenantId, {
                    text: value.description!,
                    variableTypes: extractVariablesTypes,
                }),
                ExtractVariableOverlayArea,
            );
            const newUserDefinedVariables: any = [];

            forEach(variables, (variableItem: any) => {
                if (!variableItem.standard) {
                    newUserDefinedVariables.push(variableItem);
                }
            });
            if (newUserDefinedVariables.length) {
                setSelectedTemplate(value);
                setUserDefinedVariables(newUserDefinedVariables);
            } else if (handlers!.onSelect) {
                handlers!.onSelect(value);
            }
        } else if (handlers!.onSelect) {
            handlers!.onSelect(value);
        }
    };

    const handleClose = (): void => {
        if (isFunction(cancelExpandTemplates)) {
            cancelExpandTemplates();
        }
        if (isFunction(cancelExtractVariables)) {
            cancelExtractVariables();
        }
        if (handlers!.onClose) {
            handlers!.onClose();
        }
    };

    const handleVariableSubmit = (): void => {
        const copySelectedTemplate: any = { ...selectedTemplate, ...{ isUrl: true }, ...{ userDefinedVariables } };

        if (handlers!.onSelect) {
            handlers!.onSelect(copySelectedTemplate);
        }
    };

    return (
        <div className="r4e-template-picker" onKeyPress={(event) => event.stopPropagation()} role="none">
            {displayVariableForm ? (
                <OutsideClick outsideClickHandler={handleClose}>
                    <Overlay area={ExtractVariableOverlayArea} />
                    <div className="template-info">
                        <span
                            className="close-info"
                            role="button"
                            tabIndex={0}
                            onClick={handleClose}
                            onKeyPress={(): void => {
                                // Do nothing
                            }}
                        >
                            <i className="icon-times" />
                        </span>
                        {map(userDefinedVariables, (variableItem: any, variableKey: number) => (
                            <div className="info-item" key={variableKey}>
                                <div>{variableItem.name}</div>
                                <FormControl
                                    aria-label="Template Name"
                                    value={variableItem.userDefinedValue}
                                    autoFocus={!variableKey}
                                    onChange={(e: any): void => {
                                        const newUserDefinedVariables: any[] = [...userDefinedVariables];

                                        newUserDefinedVariables[variableKey].userDefinedValue = e.target.value;
                                        setUserDefinedVariables(newUserDefinedVariables);
                                    }}
                                />
                            </div>
                        ))}
                        <Button variant="primary" onClick={handleVariableSubmit}>
                            Submit
                        </Button>
                    </div>
                </OutsideClick>
            ) : (
                <DropdownPicker
                    ref={dropdownRef}
                    searchOptions={searchOptions}
                    displayOptions={displayOptions}
                    dropdownData={templatesData}
                    hasDataLoaded={hasDataLoaded}
                    enableAutoSelect={enableAutoSelect}
                    hasOverlayPromise
                    autoFocus={autoFocus}
                    handlers={{
                        onSelect: handleSelection,
                        onOutsideClick: handleClose,
                    }}
                />
            )}
        </div>
    );
};
